import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Stethoscope, ShieldCheck, RefreshCw, ArrowLeft } from 'lucide-react';
import { authAPI } from '../../api';
import toast from 'react-hot-toast';
import { loginSuccess } from '../../store/authSlice';

export default function VerifyOTPPage() {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  
  const location = useLocation();
  const registrationData = location.state?.registrationData;

  const [timer, setTimer] = useState(() => {
    const phone = location.state?.registrationData?.phone;
    if (phone) {
      const unlockTime = localStorage.getItem(`otpUnlock_${phone}`);
      if (unlockTime) {
        const remaining = Math.floor((parseInt(unlockTime) - Date.now()) / 1000);
        if (remaining > 0) return remaining;
      }
    }
    return 30;
  });

  const [errorMsg, setErrorMsg] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasSentOTP = useRef(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!registrationData) {
      toast.error('Session expired. Please register again.');
      navigate('/register');
      return;
    }
    // We do NOT call sendOTP() here on mount anymore, because the Register API in the LocationPage already generated and sent/logged the OTP.
  }, []);

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const sendOTP = async () => {
    setResending(true);
    setErrorMsg('');
    setOtp(['', '', '', '', '', '']);
    if (inputRefs.current[0]) inputRefs.current[0].focus();

    try {
      await authAPI.sendOTP({ phone: registrationData.phone });
      toast.success('OTP sent successfully');
      setTimer(30);
      localStorage.setItem(`otpUnlock_${registrationData.phone}`, (Date.now() + 30000).toString());
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to send OTP';
      toast.error(msg);
      if (err.response?.status === 429) {
        setTimer(60); // 1 minute countdown for testing
        localStorage.setItem(`otpUnlock_${registrationData.phone}`, (Date.now() + 60000).toString());
        setErrorMsg(msg);
      }
    } finally {
      setResending(false);
    }
  };

  const handleChange = (index: number, value: string) => {
    if (isNaN(Number(value))) return;
    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // Move to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    if (!pastedData || isNaN(Number(pastedData))) return;

    const pastedDigits = pastedData.slice(0, 6).split('');
    const newOtp = [...otp];
    
    pastedDigits.forEach((digit, index) => {
      if (index < 6) {
        newOtp[index] = digit;
      }
    });
    
    setOtp(newOtp);
    
    // Focus the next empty input or the last one
    const nextEmptyIndex = newOtp.findIndex(val => val === '');
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    if (inputRefs.current[focusIndex]) {
        inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setErrorMsg('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const res = await authAPI.verifyOTP({
        phone: registrationData.phone,
        otp: otpString,
        registrationData
      });

      if (res.data.response === 'S') {
        toast.success('Registration successful! Welcome 🎉');
        if (res.data.token) {
          dispatch(loginSuccess({ token: res.data.token, seller: res.data.seller }));
        }
        navigate('/');
      } else {
        setErrorMsg(res.data.message || 'Verification failed. The OTP might be expired.');
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Invalid or expired OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-bg" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px', minHeight: '100vh', background: 'linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 40%, #faf5ff 80%, #fff7ed 100%)'
    }}>
      <div style={{ width: '100%', maxWidth: '440px', animation: 'fadeIn 0.5s ease-out' }}>
        {/* Brand & Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '18px',
            background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', boxShadow: '0 12px 32px rgba(20,184,166,0.3)',
            border: '2px solid rgba(255,255,255,0.6)'
          }}>
            <Stethoscope size={30} color="white" />
          </div>
          <h1 style={{ color: '#0f172a', fontSize: '28px', fontWeight: '800', letterSpacing: '-0.5px' }}>
            Verify <span style={{ color: '#14b8a6' }}>Phone</span>
          </h1>
          <p style={{ color: '#64748b', marginTop: '10px', fontSize: '15px', lineHeight: '1.5' }}>
            We've sent a 6-digit code to <br />
            <span style={{ color: '#0f172a', fontWeight: '600' }}>+91 {registrationData?.phone}</span>
          </p>
        </div>

        {/* Card */}
        <div style={{
            background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)',
            borderRadius: '24px', padding: '40px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
            border: '1px solid rgba(255,255,255,0.6)',
            position: 'relative', overflow: 'hidden'
        }}>
          {/* Progress highlight */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
            background: 'linear-gradient(90deg, #14b8a6, #0d9488)'
          }} />

          <form onSubmit={handleVerify}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
              <div>
                <label style={{
                  display: 'block', textAlign: 'center', color: '#475569',
                  fontSize: '14px', fontWeight: '600', marginBottom: '20px',
                  textTransform: 'uppercase', letterSpacing: '1px'
                }}>
                  Enter Verification Code
                </label>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { inputRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      style={{
                        width: '48px', height: '56px', borderRadius: '12px',
                        border: '2px solid #e2e8f0', textAlign: 'center',
                        fontSize: '22px', fontWeight: '700', color: '#0f172a',
                        background: 'white', transition: 'all 0.2s ease',
                        outline: 'none',
                        boxShadow: digit ? '0 0 0 2px #14b8a6' : 'none',
                        borderColor: digit ? '#14b8a6' : '#e2e8f0'
                      }}
                      onFocus={(e) => (e.target.style.borderColor = '#14b8a6')}
                      onBlur={(e) => (e.target.style.borderColor = digit ? '#14b8a6' : '#e2e8f0')}
                    />
                  ))}
                </div>
                {errorMsg && (
                  <p style={{ color: '#ef4444', fontSize: '13px', textAlign: 'center', marginTop: '14px', fontWeight: '600', animation: 'fadeIn 0.3s' }}>
                    {errorMsg}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
                style={{
                  width: '100%', height: '52px', borderRadius: '14px',
                  background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                  color: 'white', fontWeight: '700', fontSize: '16px',
                  border: 'none', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', gap: '10px',
                  boxShadow: '0 8px 20px rgba(20,184,166,0.25)',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
              >
                {loading ? <div className="spinner" style={{ width: '20px', height: '20px' }} /> : (
                  <>
                    <ShieldCheck size={20} />
                    Verify & Create Account
                  </>
                )}
              </button>

              <div style={{ textAlign: 'center' }}>
                {timer > 0 ? (
                  <p style={{ color: '#64748b', fontSize: '14px' }}>
                    Resend code in <span style={{ color: '#14b8a6', fontWeight: '700' }}>{formatTime(timer)}</span>
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={sendOTP}
                    disabled={resending}
                    style={{
                      background: 'none', border: 'none', color: '#14b8a6',
                      fontSize: '14px', fontWeight: '700', cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: '6px'
                    }}
                  >
                    {resending ? <RefreshCw size={14} className="spin" /> : <RefreshCw size={14} />}
                    Resend Code
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Back link */}
        <button
          onClick={() => navigate('/register')}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px', margin: '24px auto 0',
            background: 'none', border: 'none', color: '#64748b', cursor: 'pointer',
            fontSize: '14px', fontWeight: '600', transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#0f172a')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#64748b')}
        >
          <ArrowLeft size={16} /> Back to Registration
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
